import { useMediaQuery } from '@vueuse/core'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { ApiOrderType, type ApiOrderTypeValue } from '@/types/Coupon'
import { GaGeneralPageName } from '@/assets/ts/customEvent'

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

// useBreakpoints.ts
/**
 * 使用 Vueuse 的 useMediaQuery 函數來創建一個自定義的響應式斷點鉤子。
 * 定義了兩個斷點：中等大小 (isMedium) 和大型大小 (isLarge)。
 * @returns 返回包含 isMedium 和 isLarge 斷點狀態的對象。
 */
export const useBreakpoints = () => {
	const isSmall = useMediaQuery('(max-width: 767px)')
	// 定義中等尺寸斷點，屏幕寬度大於等於 768px 時為 true。
	const isMedium = useMediaQuery('(min-width: 768px) and (max-width: 1279px)')
	// 定義大型尺寸斷點，屏幕寬度大於等於 1280px 時為 true。
	const isLarge = useMediaQuery('(min-width: 1280px)')
	// 定義特大型尺寸斷點，屏幕寬度大於等於 1800px 時為 true。
	const isLargest = useMediaQuery('(min-width: 1800px)')
	// For old project breaking point
	const isLargeOld = useMediaQuery('(min-width: 1200px)')

	const isTabletPortrait = useMediaQuery('(min-width: 768px)')
	const isTabletLandscape = useMediaQuery('(min-width: 1024px)')

	const isTabletPortraitScreen = useMediaQuery(
		'(min-width: 768px) and (max-width: 1023px)',
	)
	const isTabletLandscapeScreen = useMediaQuery(
		'(min-width: 1024px) and (max-width: 1279px)',
	)

	return {
		isSmall,
		isMedium,
		isLarge,
		isLargeOld,
		isLargest,
		isTabletPortrait,
		isTabletLandscape,
		isTabletPortraitScreen,
		isTabletLandscapeScreen,
	}
}

export const breakPointVariables = {
	mobile: '768',
	tabletLandscape: '1024',
	desktop: '1280',
}

// fix src path in mode amazon
export const formatSrc = (src: string) => {
	if (import.meta.env.PUBLIC_ENVIRONMENT === 'amazon') {
		return `..${src}`
	}
	return src
}

/**
 * Returns the full path to an asset image, based on the environment.
 *
 * @param {string} src - The relative path to the image asset.
 *
 * @returns {string} - The full URL (production) or relative file path (local).
 *
 * Example (Production):
 * `https://b2c-cdn.buynship.com/websites/buyandship-2019/builds/2031/.../avatar_2_48px@2x.png`
 *
 * Example (Local):
 * `../src/assets/images/avatar_2_48px@2x.png`
 */
export const getVueAssetImagePath = (src: string) => {
	const settingsStaticUrl = window.BUYANDSHIP.settings.static_url
	const envStaticImgUrl = import.meta.env.PUBLIC_STATIC_IMG_URL
	return `${settingsStaticUrl}${envStaticImgUrl}${src}`
}

/**
 * Formats the display price.
 * @param price - The price to format.
 * @returns The formatted display price as a string.
 */
export const formatDisplayPrice = (price: number): string => {
	return price.toLocaleString(undefined, { minimumFractionDigits: 2 })
}

/**
 * Formats a date string to the format YYYY-MM-DD.
 * @param dateString - The date string to be formatted.
 * @returns The formatted date string in the format YYYY-MM-DD.
 */
export const formatDateToYYYYMMDD = (dateString: string): string => {
	const date = new Date(dateString)
	return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0]
}

/**
 * Capitalizes the first letter of a string.
 *
 * @param text - The input string.
 * @returns The input string with the first letter capitalized.
 */
export const capitalizeFirstLetter = (text: string) => {
	if (!text || typeof text !== 'string') {
		return text
	}
	return text.charAt(0).toUpperCase() + text.slice(1)
}
export class variableTextTranslator {
	text: string

	inviteBonus: string

	welcomeBonus: string

	constructor(text: string) {
		this.text = text
		this.inviteBonus =
			String(window.BUYANDSHIP?.settings?.member_invite_host_credits) || ''
		this.welcomeBonus =
			String(window.BUYANDSHIP?.settings?.member_welcome_credits) || ''
	}

	get value() {
		return this.text
	}

	getInviteCredit() {
		this.text = this.text.replace(/{INVITE_CREDIT}/g, String(this.inviteBonus))
		return this
	}

	getWelcomeCredit() {
		this.text = this.text.replace(
			/{WELCOME_CREDIT}/g,
			String(this.welcomeBonus),
		)
		return this
	}
}

export function getCreditText(text: string) {
	return new variableTextTranslator(text).getInviteCredit().getWelcomeCredit()
		.value
}

/* eslint-disable */
var lut: number[] | string[] = []
for (var i = 0; i < 256; i++) {
	lut[i] = (i < 16 ? '0' : '') + i.toString(16)
}
export function generateUUID() {
	var k = [
		'x',
		'x',
		'x',
		'x',
		'-',
		'x',
		'x',
		'-',
		'4',
		'x',
		'-',
		'y',
		'x',
		'-',
		'x',
		'x',
		'x',
		'x',
		'x',
		'x',
	]
	var u = '',
		i = 0,
		rb = (Math.random() * 0xffffffff) | 0
	while (i++ < 20) {
		var c = k[i - 1],
			r = rb & 0xff,
			v = c == 'x' ? r : c == 'y' ? (r & 0x3f) | 0x80 : (r & 0xf) | 0x40
		u += c == '-' ? c : lut[v]
		rb = i % 4 == 0 ? (Math.random() * 0xffffffff) | 0 : rb >> 8
	}
	return u
}

export async function copyText(text: string) {
	if (navigator.clipboard && window.isSecureContext) {
		await navigator.clipboard.writeText(text)
	} else {
		const textarea = document.createElement('textarea')
		textarea.value = text
		textarea.style.position = 'absolute'
		textarea.style.left = '-9999px'
		document.body.appendChild(textarea)
		textarea.select()
		document.execCommand('copy')
		document.body.removeChild(textarea)
	}
}

export function checkIsKeyupEsc(evt: KeyboardEvent) {
	return evt.key === 'Escape' || evt.key === 'Esc' || evt.keyCode === 27
}

export function getCouponEventExtra(
	orderType: ApiOrderTypeValue,
	couponCode: string,
): string {
	const couponTypePrefixMap: Record<ApiOrderTypeValue, string> = {
		[ApiOrderType.Shipping]: 'bns',
		[ApiOrderType.ProxyShopping]: 'b4u',
		[ApiOrderType.ExpressCheckout]: 'ec',
	}

	const prefix = couponTypePrefixMap[orderType]
	return `${prefix}_${couponCode}`
}

export function setCurrentGaPageName(pageName: GaGeneralPageName) {
	window.gaPageName = pageName
}

export function getCurrentGaPageName() {
	return window.gaPageName || ''
}

export function setIntercomDisplay(isShow: boolean) {
	const status = isShow ? '' : 'none'
	const targets = ['.intercom-lightweight-app', '.intercom-app']
	for (let i = 0; i < targets.length; i += 1) {
		const element = document.querySelector<HTMLElement>(targets[i])
		if (element !== null) {
			element.style.display = status
			break
		}
	}
}
